/**
 * Audit-Event Labels — DE single-source für Activity-Feed (Sprint B PR-A DK-18).
 *
 * Konsumenten:
 *  - Activity-Feed (PR-B Dashboard-Section)
 *  - /audit-log Admin-UI (separater Sprint)
 *
 * Details-Shape pro Event (canonical: `version:1` als FIRST-KEY, camelCase
 * payload — patterns/api.md § Audit-Shape Key-Order-Invariante):
 *   share.created:             { version:1, targetType:'asset'|'collection', targetId, hasExpiry }
 *   share.accessed:            { version:1, shareId }
 *   share.accessed_dead:       { version:1, shareId }
 *   share.downloaded:          { version:1, shareId }
 *   share.revoked:             { version:1, shareId }
 *   share.extended:            { version:1, days, previousExpiresAt, expiresAt, wasExpired, admin_override? }
 *   share.link_revealed:       { version:1, targetType, targetId }                         // nie URL/Token
 *   share.zip_downloaded:      { version:1, shareId, collectionId, assetCount, totalBytes, failedCount, durationMs, aborted }
 *   share.malformed_row:       { version:1, shareId, hasAssetId, hasCollectionId }      // OPERATIONAL, NICHT user-facing
 *   collection.created:        { version:1, name, descriptionSet }
 *   collection.updated:        { version:1, changes: string[] }
 *   collection.deleted:        { version:1, adminOverride }
 *   collection.assets_added:   { version:1, collectionId, addedCount, assetIds: string[] }
 *   collection.asset_removed:  { version:1, collectionId, assetId }
 *   collection.zip_downloaded: { version:1, collectionId, assetCount, totalBytes, failedCount, durationMs, aborted, adminOverride }
 *
 * Default-Fallback `getEventLabel(unknown)` returnt `{label_de: unknown, icon: 'help'}`
 * damit unbekannte events bei rolling-deploys oder DB-state-drift nicht crashen.
 */

export type AuditEventLabel = {
  label_de: string;
  /** lucide-react icon-name (string-keyed um zwei Deps zu vermeiden). */
  icon: string;
};

const LABELS: Record<string, AuditEventLabel> = {
  'share.created': {
    label_de: 'Share-Link erstellt',
    icon: 'link',
  },
  'share.accessed': {
    label_de: 'Share-Link aufgerufen',
    icon: 'eye',
  },
  'share.accessed_dead': {
    label_de: 'Share-Link aufgerufen, Asset im Papierkorb',
    icon: 'archive',
  },
  'share.downloaded': {
    label_de: 'Share-Link Download',
    icon: 'download',
  },
  'share.revoked': {
    label_de: 'Share-Link widerrufen',
    icon: 'x-circle',
  },
  'share.extended': {
    label_de: 'Share-Link verlängert',
    icon: 'rotate-ccw',
  },
  'share.link_revealed': {
    label_de: 'Share-Link kopiert',
    icon: 'copy',
  },
  'share.zip_downloaded': {
    label_de: 'Share-Link ZIP-Download',
    icon: 'download',
  },
  // OPERATIONAL event (Codex R2 Contract-2) — KEIN user-facing Activity-Feed-Item.
  // Triggered von `validateShareTarget` bei malformed share-row (both/neither ids).
  // Ops-Query: `WHERE event = 'share.malformed_row'` für leakage-detection.
  'share.malformed_row': {
    label_de: 'Share-Link malformed data',
    icon: 'x-circle',
  },
  // Collection events (Sprint B PR-B1 DK-21 + PR-B2 DK-2).
  'collection.created': {
    label_de: 'Collection erstellt',
    icon: 'folder-plus',
  },
  'collection.updated': {
    label_de: 'Collection bearbeitet',
    icon: 'edit',
  },
  'collection.deleted': {
    label_de: 'Collection gelöscht',
    icon: 'trash',
  },
  'collection.assets_added': {
    label_de: 'Assets zu Collection hinzugefügt',
    icon: 'folder-input',
  },
  'collection.asset_removed': {
    label_de: 'Asset aus Collection entfernt',
    icon: 'folder-minus',
  },
  'collection.zip_downloaded': {
    label_de: 'Collection als ZIP heruntergeladen',
    icon: 'download',
  },
  // Asset events (Sprint B PR-B3 DK-15). Canonical camelCase details, version:1 FIRST-KEY.
  //   asset.uploaded:            { version:1, assetId, displayFilename, fileSize, assetType }
  //   asset.tagged:              { version:1, assetId, addedTags: string[], removedTags: string[] }
  //   asset.renamed:             { version:1, assetId, oldFilename, newFilename }
  //   asset.description_changed: { version:1, assetId, hadOldDescription, hasNewDescription }
  //   asset.status_changed:      { version:1, assetId, oldStatus, newStatus }
  //   asset.deleted:             { version:1, assetId, displayFilename }  // KEIN adminOverride (asset-DELETE hat keine owner-restriction)
  //   asset.restored:            { version:1, assetId, displayFilename }
  'asset.uploaded': {
    label_de: 'Asset hochgeladen',
    icon: 'upload',
  },
  'asset.tagged': {
    label_de: 'Tags geändert',
    icon: 'tag',
  },
  'asset.renamed': {
    label_de: 'Asset umbenannt',
    icon: 'edit',
  },
  'asset.description_changed': {
    label_de: 'Beschreibung bearbeitet',
    icon: 'edit',
  },
  'asset.status_changed': {
    label_de: 'Status geändert',
    icon: 'check-circle',
  },
  'asset.deleted': {
    label_de: 'Asset gelöscht',
    icon: 'trash',
  },
  'asset.restored': {
    label_de: 'Asset wiederhergestellt',
    icon: 'rotate-ccw',
  },
  // Vokabular (Taxonomie v2) — Kategorie/Produkt/Verwendung.
  'vocab.kategorie.created': { label_de: 'Kategorie angelegt', icon: 'plus' },
  'vocab.kategorie.updated': { label_de: 'Kategorie umbenannt', icon: 'edit' },
  'vocab.kategorie.deleted': { label_de: 'Kategorie gelöscht', icon: 'trash' },
  'vocab.produkt.created': { label_de: 'Produkt angelegt', icon: 'plus' },
  'vocab.produkt.updated': { label_de: 'Produkt umbenannt', icon: 'edit' },
  'vocab.produkt.deleted': { label_de: 'Produkt gelöscht', icon: 'trash' },
  'vocab.verwendung.created': { label_de: 'Verwendung angelegt', icon: 'plus' },
  'vocab.verwendung.updated': { label_de: 'Verwendung umbenannt', icon: 'edit' },
  'vocab.verwendung.deleted': { label_de: 'Verwendung gelöscht', icon: 'trash' },
  // Transfer-Feature (Sprint T1, 2026-07-08).
  //   transfer.finalized: { version:1, transferId, fileCount, totalBytes, expiresAt }
  //   transfer.revoked:   { version:1, transferId, wasDraft }
  //   transfer.accessed:  { version:1, transferId }
  'transfer.finalized': { label_de: 'Transfer verschickt', icon: 'send' },
  'transfer.revoked': { label_de: 'Transfer widerrufen', icon: 'x-circle' },
  'transfer.accessed': { label_de: 'Transfer abgerufen', icon: 'download' },
};

/**
 * Lookup mit Default-Fallback. Unknown event-keys returnen ihren raw-key als
 * label + 'help'-icon, damit der Feed nie crasht. PR-B erweitert LABELS um
 * collection.* + zip.* events.
 */
export function getEventLabel(eventKey: string): AuditEventLabel {
  return LABELS[eventKey] ?? { label_de: eventKey, icon: 'help' };
}
